import ResponsiveTable, { ResponsiveCell } from '../shared/ResponsiveTable';
import '../financials/financialsResponsive.css';
import ResponsiveRow from '../shared/ResponsiveRow';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import HoverHelp from '../shared/HoverHelp';
import ActionPillMenu from '../ui/ActionPillMenu';
import useModuleAccess from '../../hooks/useModuleAccess';

const typeLabel = (type) => (type === 'All' ? 'All Types' : type === 'income' ? 'Income' : 'Expense');

const CHEVRON_ICON = (
    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
);

function TransactionsView({ transactions, chartOfAccounts, onAddTransaction, onEditTransaction, onDeleteTransaction, theme = 'light', userId, isUser = false }) {
    const { allowed: financialEditAllowed } = useModuleAccess(isUser ? 'financial' : null, 'edit');
    const canEdit = !isUser || financialEditAllowed;
    const [filterCategory, setFilterCategory] = useState('All');
    const [filterType, setFilterType] = useState('All');
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedTransactionId, setExpandedTransactionId] = useState(null);
     const filteredTransactions = useMemo(() => {
        return transactions.filter(t => {
            const matchesCategory = filterCategory === 'All' || t.category === filterCategory;
            const matchesType = filterType === 'All' || t.type === filterType;
            const matchesSearch = !searchTerm || 
                (t.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (t.category || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (t.merchant || '').toLowerCase().includes(searchTerm.toLowerCase());
            return matchesCategory && matchesType && matchesSearch;
        });
    }, [transactions, filterCategory, filterType, searchTerm]);
     const categories = ['All', ...new Set(chartOfAccounts.map(c => c.name))];
    const filterTriggerCls = `px-4 py-2 text-sm font-medium rounded-lg border transition-colors inline-flex items-center gap-2 ${
        theme === 'dark'
            ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600'
            : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
    }`;
     return (
        <div className="ia-financials">
            <ResponsiveRow className="ia-financial-header flex justify-between items-center mb-6">
                <div className="flex items-center gap-4">
                    <div>
                        <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-gray-100' : 'text-gray-800'}`}>Banking Transactions</h2>
                        <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Track income and expenses</p>
                    </div>
                </div>
                {canEdit && <button onClick={onAddTransaction} className="bg-[var(--btn-fill)] text-[var(--btn-ink)] px-6 py-3 rounded-lg font-medium hover:bg-[var(--btn-fill-hover)] transition flex items-center gap-2">
                    <span>➕</span> Add Transaction
                </button>}
            </ResponsiveRow>
             {/* Filters */}
            <div className={`rounded-lg shadow-md p-4 mb-6 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                <ResponsiveRow className="ia-financial-filters flex gap-4">
                    <div className="flex flex-1 min-w-[220px] items-center">
                        <input
                            type="text"
                            placeholder="Search transactions..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className={`flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--accent-ink)] ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                        />
                        <HoverHelp helpId="financials.transactions.search" userId={userId} />
                    </div>

                    {/* Type and Category filters — BL-177 Gate 17d Action Pill menus. */}
                    <div className="relative inline-flex items-center">
                        <ActionPillMenu
                            menuLabel="Filter by type"
                            placement="bottom"
                            align="end"
                            options={['All', 'income', 'expense'].map((type) => ({
                                key: type,
                                label: typeLabel(type),
                                active: filterType === type,
                                onSelect: () => setFilterType(type),
                            }))}
                            triggerClassName={filterTriggerCls}
                        >
                            <span>{typeLabel(filterType)}</span>
                            {CHEVRON_ICON}
                        </ActionPillMenu>
                        <HoverHelp helpId="financials.transactions.type-filter" userId={userId} />
                    </div>

                    <div className="relative inline-flex items-center">
                        <ActionPillMenu
                            menuLabel="Filter by category"
                            placement="bottom"
                            align="end"
                            scrollable
                            options={categories.map((cat) => ({
                                key: cat,
                                label: cat,
                                active: filterCategory === cat,
                                onSelect: () => setFilterCategory(cat),
                            }))}
                            triggerClassName={filterTriggerCls}
                        >
                            <span>{filterCategory}</span>
                            {CHEVRON_ICON}
                        </ActionPillMenu>
                        <HoverHelp helpId="financials.transactions.category-filter" userId={userId} />
                    </div>
                </ResponsiveRow>
            </div>
              {/* Transactions Table */}
            <div className={`rounded-lg shadow-md ia-financial-records overflow-hidden ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                <ResponsiveTable className="w-full">
                    <thead className={`${theme === 'dark' ? 'bg-gray-900/40 border-gray-700' : 'bg-gray-50 border-gray-200'} border-b`}>
                        <tr>
                            <th className={`px-6 py-3 text-left text-xs font-medium uppercase ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Date</th>
                            <th className={`px-6 py-3 text-left text-xs font-medium uppercase ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Description</th>
                            <th className={`px-6 py-3 text-left text-xs font-medium uppercase ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Category</th>
                            <th className={`px-6 py-3 text-left text-xs font-medium uppercase ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Type</th>
                            <th className={`px-6 py-3 text-right text-xs font-medium uppercase ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Amount</th>
                            {canEdit && <th className={`px-6 py-3 text-right text-xs font-medium uppercase ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Actions</th>}
                        </tr>
                    </thead>
                    <tbody className={`${theme === 'dark' ? 'divide-y divide-gray-700' : 'divide-y divide-gray-200'}`}>
                        {filteredTransactions.length === 0 && (
                            <tr>
                                <td colSpan={canEdit ? 6 : 5} className="px-6 py-12 text-center">
                                    <div className="text-gray-500">
                                        <div className="text-6xl mb-4">💰</div>
                                        <h3 className="text-xl font-semibold mb-2">No transactions yet</h3>
                                        <p className="mb-4">Add your first transaction to start tracking finances</p>
                                        {canEdit && <button onClick={onAddTransaction} className="bg-[var(--btn-fill)] text-[var(--btn-ink)] px-6 py-2 rounded-lg font-medium hover:bg-[var(--btn-fill-hover)] transition">
                                            Add Transaction
                                        </button>}
                                    </div>
                                </td>
                            </tr>
                        )}
                        {filteredTransactions.map(transaction => (
                            <React.Fragment key={transaction.id}>
                                <tr 
                                    className={`cursor-pointer transition ${theme === 'dark' ? 'hover:bg-gray-700/40' : 'hover:bg-gray-50'}`}
                                    onClick={() => setExpandedTransactionId(expandedTransactionId === transaction.id ? null : transaction.id)}
                                >
                                    <ResponsiveCell label="Date" className={`px-6 py-4 text-sm ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>{new Date(transaction.date).toLocaleDateString()}</ResponsiveCell>
                                    <ResponsiveCell label="Description" className="px-6 py-4">
                                        <div className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>{transaction.description}</div>
                                        {transaction.merchant && <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{transaction.merchant}</div>}
                                    </ResponsiveCell>
                                    <ResponsiveCell label="Category" className={`px-6 py-4 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>{transaction.category}</ResponsiveCell>
                                    <ResponsiveCell label="Type" className="px-6 py-4">
                                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                            transaction.type === 'income' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                        }`}>
                                            {transaction.type}
                                        </span>
                                    </ResponsiveCell>
                                    <ResponsiveCell label="Amount" className="ia-financial-amount px-6 py-4 text-right">
                                        <span className={`text-sm font-semibold ${transaction.type === 'income' || transaction.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                            {transaction.type === 'income' || transaction.amount > 0 ? '+' : '-'}
                                            ${Math.abs(transaction.amount || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}
                                        </span>
                                    </ResponsiveCell>
                                    {canEdit && <ResponsiveCell label="Actions" className="px-6 py-4 text-right">
                                        <button 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onEditTransaction(transaction);
                                            }} 
                                            className="text-blue-600 hover:text-blue-800 mr-3" 
                                            title="Edit"
                                        >
                                            ✏️
                                        </button>
                                        <button 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDeleteTransaction(transaction.id);
                                            }} 
                                            className="text-red-600 hover:text-red-800" 
                                            title="Delete"
                                        >
                                            🗑️
                                        </button>
                                    </ResponsiveCell>}
                                </tr>
                                {expandedTransactionId === transaction.id && (
                                    <tr className={theme === 'dark' ? 'bg-blue-900/20' : 'bg-blue-50'}>
                                        <td colSpan={canEdit ? 6 : 5} className="px-6 py-4">
                                            <div className={`rounded-lg p-6 shadow-inner ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                                                <h4 className={`font-semibold text-lg mb-4 ${theme === 'dark' ? 'text-gray-100' : 'text-gray-800'}`}>Transaction Details</h4>
                                                <div className="grid grid-cols-2 gap-6">
                                                    <div>
                                                        <div className="mb-4">
                                                            <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Date</label>
                                                            <div className={theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}>{new Date(transaction.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
                                                        </div>
                                                        <div className="mb-4">
                                                            <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Description</label>
                                                            <div className={theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}>{transaction.description}</div>
                                                        </div>
                                                        <div className="mb-4">
                                                            <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Merchant</label>
                                                            <div className={theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}>{transaction.merchant || 'N/A'}</div>
                                                        </div>
                                                        <div className="mb-4">
                                                            <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>From/To</label>
                                                            <div className={theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}>{transaction.from_to || 'N/A'}</div>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="mb-4">
                                                            <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Category</label>
                                                            <div className={theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}>{transaction.category}</div>
                                                        </div>
                                                        <div className="mb-4">
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                                                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                                                transaction.type === 'income' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                            }`}>
                                                                {transaction.type}
                                                            </span>
                                                        </div>
                                                        <div className="mb-4">
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                                                            <div className={`text-xl font-bold ${transaction.type === 'income' || transaction.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                                {transaction.type === 'income' || transaction.amount > 0 ? '+' : '-'}
                                                                ${Math.abs(transaction.amount || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}
                                                            </div>
                                                        </div>
                                                        <div className="mb-4">
                                                            <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Tags</label>
                                                            <div className={theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}>{transaction.tags || 'No tags'}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                                {transaction.memo && (
                                                    <div className="mt-4 pt-4 border-t">
                                                        <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Memo</label>
                                                        <div className={`p-3 rounded ${theme === 'dark' ? 'text-gray-100 bg-gray-900/30' : 'text-gray-900 bg-gray-50'}`}>{transaction.memo}</div>
                                                    </div>
                                                )}
                                                <div className="mt-4 flex gap-2">
                                                    {canEdit && <>
                                                        <button
                                                            onClick={() => onEditTransaction(transaction)}
                                                            className="bg-[var(--btn-fill)] text-[var(--btn-ink)] px-4 py-2 rounded-lg hover:bg-[var(--btn-fill-hover)] transition"
                                                        >
                                                            ✏️ Edit Transaction
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                if (window.confirm('Are you sure you want to delete this transaction?')) {
                                                                    onDeleteTransaction(transaction.id);
                                                                    setExpandedTransactionId(null);
                                                                }
                                                            }}
                                                            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition"
                                                        >
                                                            🗑️ Delete
                                                        </button>
                                                    </>}
                                                    <button 
                                                        onClick={() => setExpandedTransactionId(null)}
                                                        className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition ml-auto"
                                                    >
                                                        Close
                                                    </button>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </ResponsiveTable>
            </div>
        </div>
    );
}


export default TransactionsView;
